/**
 * Feature Maturity Designation System
 *
 * Four-tier system for indicating feature readiness:
 * - coming_soon: Not yet available, non-interactive
 * - prototype: Early proof-of-concept, interactive but unstable
 * - alpha: Functional but incomplete, interactive
 * - demo: Working demonstration (e.g., regtest), interactive
 *
 * No designation (undefined/null) = production-ready, no badge shown.
 */

export type FeatureDesignation = "coming_soon" | "prototype" | "alpha" | "demo";

export const DESIGNATION_STYLES: Record<
  FeatureDesignation,
  { bg: string; text: string; label: string }
> = {
  coming_soon: {
    bg: "bg-sa-surface-hover",
    text: "text-sa-text-muted",
    label: "Coming Soon",
  },
  prototype: {
    bg: "bg-purple-500/10",
    text: "text-purple-500",
    label: "Prototype",
  },
  alpha: {
    bg: "bg-sa-warning/10",
    text: "text-sa-warning",
    label: "Alpha",
  },
  demo: {
    bg: "bg-primary/10",
    text: "text-primary",
    label: "Demo",
  },
};

export function isInteractive(designation?: FeatureDesignation | null): boolean {
  return designation !== "coming_soon";
}

/**
 * Resolved designation metadata returned by getDesignation().
 * Includes all information needed for UI rendering.
 */
export interface FeatureDesignationMeta {
  /** Feature identifier from the manifest */
  id: string;
  /** Designation tier */
  designation: FeatureDesignation;
  /** Human-readable label (e.g., "Coming Soon", "Prototype") */
  label: string;
  /** CSS classes for badge background and text */
  badgeClass: string;
  /** Text to display in watermark overlay */
  watermarkText: string;
  /** Whether the feature is interactive (false only for coming_soon) */
  isInteractive: boolean;
}

/**
 * Feature entry in the manifest.
 * Mirrors the YAML schema from feature-manifest.yaml.
 */
export interface ManifestFeature {
  id: string;
  name: string;
  designation?: FeatureDesignation;
  description?: string;
  mock_data_source?: string;
  backend_dependencies?: Array<{
    type: "smart_contract" | "database" | "depin_service";
    name: string;
    status:
      | "not_started"
      | "planned"
      | "in_progress"
      | "partial"
      | "deployed_testnet"
      | "deployed_production";
  }>;
}

/**
 * Feature manifest schema.
 * Represents the parsed feature-manifest.yaml structure.
 */
export interface FeatureManifest {
  version: string;
  repository: string;
  features: ManifestFeature[];
}
